import { observer } from "mobx-react-lite";
import { useRootStore } from "../../../stores/hooks";
import type { ActionGroupType } from "../../../types/candidate";
import type { CurrSection } from "../../../containers/AutomationForm/types";
import { BranchColumn } from "./BranchColumn";
import {
  StyledBranchRow,
  StyledBranchColumnWrap,
  StyledConnectorArrow,
} from "./style";

interface NestedBranchesFanProps {
  /** instanceId of the branches block whose branches to fan out */
  instanceId: number;
  activeItem: CurrSection;
  activeAction: string | undefined;
  onSelect: (instanceId: number, branchHash: string) => void;
  onClickAction: (hash?: string) => void;
  onClickTrigger: () => void;
  onChangeAction: (
    integrationId: string,
    actionId: string,
    actionGroupType?: ActionGroupType,
  ) => void;
  onSelectNestedBlock: (instanceId: number) => void;
  onSelectNestedLoop: (instanceId: number) => void;
}

export const NestedBranchesFan = observer<NestedBranchesFanProps>(
  ({
    instanceId,
    activeItem,
    activeAction,
    onSelect,
    onClickAction,
    onClickTrigger,
    onChangeAction,
    onSelectNestedBlock,
    onSelectNestedLoop,
  }) => {
    const store = useRootStore();
    const block = store.automations.candidate.branches.findBlock(instanceId);
    if (block === undefined) return null;

    const branchHashes: Array<string | "else"> = [
      ...block.branches.map((b) => b.hash),
      ...(block.elseBranch !== undefined ? (["else"] as const) : []),
    ];
    const total =
      block.branches.length + (block.elseBranch !== undefined ? 1 : 0);

    return (
      <StyledBranchRow data-testid={`branches-connector-row-${instanceId}`}>
        {branchHashes.map((branchHash, index) => (
          <StyledBranchColumnWrap
            key={String(branchHash)}
            isFirst={index === 0}
            isLast={index === branchHashes.length - 1}
          >
            <StyledConnectorArrow />
            <BranchColumn
              blockInstanceId={instanceId}
              branchHash={branchHash}
              isSelected={
                activeItem?.type === "branch" &&
                activeItem.instanceId === instanceId &&
                activeItem.branchHash === branchHash
              }
              onSelect={onSelect}
              activeAction={activeAction}
              onClickAction={onClickAction}
              onClickTrigger={onClickTrigger}
              onChangeAction={onChangeAction}
              isDeletable={total > store.featureFlags.limits.minTotalBranches}
              onBranchDeleted={onSelectNestedBlock}
              onSelectNestedBlock={onSelectNestedBlock}
              onSelectNestedLoop={onSelectNestedLoop}
              activeItem={activeItem}
            />
          </StyledBranchColumnWrap>
        ))}
      </StyledBranchRow>
    );
  },
);
NestedBranchesFan.displayName = "NestedBranchesFan";
